import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LocalStorage } from '@isophi/core-legacy';
import { Child, PopUpComponent } from '@isophi/teachers-shared';
import { TranslocoService } from '@jsverse/transloco';
import { map, of, Subscription, switchMap } from 'rxjs';

import { RouterLinkFactory } from '../../../../core/router/router-link.factory';
import { ChildService } from '../../../../core/services/child.service';
import { ChildArchiveService } from '../../../../core/services/child-archive.service';
import { KindergartenService } from '../../../../core/services/kindergarten.service';

@Component({
    selector: 'app-archive-notification',
    templateUrl: './archive-notification.component.html',
    standalone: false
})
/**
 * Shows popup once in a year reminding of the possibility to archive children.
 */
export class ArchiveNotificationComponent implements OnInit, OnDestroy {
  public static readonly YEAR_WAS_SHOWN_KEY = 'yearArchiveNotificationWasShown';

  private readonly kindergartenService = inject(KindergartenService);

  @ViewChild('archiveNotificationPopupContent', { static: true })
  public archiveNotificationPopupContent: ElementRef;

  private storage: LocalStorage;

  private _sub: Subscription;

  public constructor(
    private popUp: PopUpComponent,
    private router: Router,
    private routerLinkFactory: RouterLinkFactory,
    private childArchiveService: ChildArchiveService,
    private childService: ChildService,
    private translocoService: TranslocoService
  ) {}

  public async ngOnInit(): Promise<void> {
    this.storage = new LocalStorage();
    const dateInstance = new Date();
    const yearWasShownKey = this.storage.getItem(ArchiveNotificationComponent.YEAR_WAS_SHOWN_KEY);
    const wasPopupShownThisYear = yearWasShownKey === dateInstance.getFullYear().toString();
    // The right part of year == from August (7) to November (10)
    const isRightPartOfYear = dateInstance.getMonth() >= 7 && dateInstance.getMonth() <= 10;

    // CHECK the conditions for displaying the popup, starting with the simpler ones
    if (!isRightPartOfYear || wasPopupShownThisYear) return;

    // Search for children in archive from the current year
    const currentSchoolYear = `${dateInstance.getFullYear() - 1}/${dateInstance.getFullYear() - 2000}`;

    if (this._sub) {
      this._sub.unsubscribe();
    }

    this._sub = this.childArchiveService
      .children(currentSchoolYear)
      .pipe(
        switchMap((children: Child[]) => {
          const archivedChildren = children || [];

          if (this.kindergartenService.loggedKindergarten.isCZSchool()) {
            return of(false);
          }

          // CHECK if there was not already some child archived this year
          if (archivedChildren.length < 1) {
            return this.childService.getChildren().pipe(
              map((currentChildren: Child[]) => {
                for (const child of currentChildren) {
                  // Seek any child older than six years
                  if (child.ageInDays > 365 * 6) {
                    return true;
                  }
                }
                return false;
              })
            );
          } else {
            return of(false);
          }
        })
      )
      .subscribe((show: boolean) => {
        if (show) this.showPopup();
      });
  }

  public async showPopup(): Promise<void> {
    const messageTitle = this.translocoService.translate('shared/archiveProfile');
    const confirmText = this.archiveNotificationPopupContent.nativeElement.innerHTML;
    const yesText = this.translocoService.translate('shared/goToArchive');
    const noText = this.translocoService.translate('shared/notNow');
    const confirm = await this.popUp.confirm(messageTitle, confirmText, null, yesText, noText, 'btn-secondary', 'btn-outline-dark', 'lg');

    // Save that the popup was shown to the local storage
    const dateInstance = new Date();
    this.storage.setItem(ArchiveNotificationComponent.YEAR_WAS_SHOWN_KEY, dateInstance.getFullYear().toString(), true);

    // Close the pop up
    if (!confirm) return;

    // Proceed to the child list - where there is the archive option
    await this.router.navigate(this.routerLinkFactory.childList());
  }

  ngOnDestroy(): void {
    if (this._sub) {
      this._sub.unsubscribe();
    }
  }
}
